// First screen for signed-out visitors: what NinjaEval does, proof from the live MediBook demo, a way in.
import React, { useEffect, useState } from 'react';
import { api, GOOGLE_CLIENT_ID } from './engApi';
import { pct, CopyCode } from './ui';

// Brand: the Kensa avatar + "NinjaEval" lockup; the mark alone on small screens.
export function Logo({ big }) {
  return <span className={'ef-brand' + (big ? ' big' : '')}>
    <img className="lockup" src="/brand/lockup-light.png" alt="NinjaEval" />
    <img className="mark" src="/brand/avatar-64.png" alt="NinjaEval" /></span>;
}

// Kensa (検査, "inspection"): one face per verdict, matching PASS / WARN / FAIL.
export function Kensa({ v = 'pass', size = 40, title }) {
  const k = { pass: 'pass', ready: 'pass', warn: 'warn', fail: 'fail', blocked: 'fail' }[v] || 'pass';
  return <img src={`/brand/kensa-${k}.png`} width={size} height={size} alt={title || k} title={title} style={{ objectFit: 'contain', flexShrink: 0 }} />;
}

function loadGoogle(cb) {
  if (window.google?.accounts?.id) return cb();
  const el = document.createElement('script');
  el.src = 'https://accounts.google.com/gsi/client'; el.async = true; el.onload = cb;
  document.head.appendChild(el);
  return undefined;
}

let gsiReady = false;

// Get a fresh ID token without a click, for a user who already signed in (Google auto-select). Best effort: if the
// browser or Google won't, the token expires and the app asks the user to sign in again.
export function silentRefresh(onFresh) {
  if (!GOOGLE_CLIENT_ID) return;
  loadGoogle(() => {
    window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, auto_select: true, callback: (r) => onFresh(r.credential) });
    gsiReady = false;                          // the next sign-in button re-initialises with its own callback
    window.google.accounts.id.prompt();
  });
}
export function GoogleButton({ onToken, text = 'signin_with' }) {
  const [id] = useState(() => 'gsi-' + Math.random().toString(36).slice(2));
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    loadGoogle(() => {
      if (!gsiReady) { window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: (r) => onToken(r.credential) }); gsiReady = true; }
      const el = document.getElementById(id);
      if (el) window.google.accounts.id.renderButton(el, { theme: 'filled_blue', size: 'large', shape: 'pill', text });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!GOOGLE_CLIENT_ID) return <div className="ef-err">Sign-in isn't configured on this deployment.</div>;
  return <div id={id} style={{ minHeight: 44 }} />;
}

// The real story, read from the demo: the blocked change, why, the fix, and what shipped.
export function useProof() {
  const [proof, setProof] = useState(null);
  useEffect(() => {
    (async () => {
      const p = await api.project('medibook');
      const shipped = p.change_tests.find((t) => t.status === 'shipped');
      const blockedId = shipped?.parent_test_id || p.change_tests.find((t) => t.attempt === 1)?.test_id;
      if (!shipped || !blockedId) return;
      const b = await api.test(blockedId);
      const reg = b.rows.filter((r) => r.kind === 'regressed');
      const diag = reg.map((r) => r.rca?.result).find((x) => x?.diagnosis);
      setProof({ p, shipped, blocked: b, reg, diag });
    })().catch(() => {});
  }, []);
  return proof;
}

// first sentence, ignoring full stops inside quotes ('You are not a doctor.')
function firstSentence(t = '') {
  let q = 0;
  for (let i = 0; i < t.length; i++) {
    if (t[i] === "'" && /\s|^$/.test(t[i - 1] || '')) q++;                  // opening: after a space
    else if (t[i] === "'" && !/\w/.test(t[i + 1] || '')) q = Math.max(0, q - 1);   // closing; "don't" is neither
    if (t[i] === '.' && q <= 0 && (t[i + 1] === ' ' || i === t.length - 1)) return t.slice(0, i + 1);
  }
  return t;
}

// The idea in one picture: your change → your tests on both versions → a verdict with the reason and the fix.
export function Explainer({ compact }) {
  const rows = [['Book a dental check-up', 'p', 'p'], ['“Which medicine should I take?”', 'p', 'f'], ['Chest pain (emergency)', 'p', 'p']];
  const mark = { p: <span className="ef-st p">✓ Pass</span>, f: <span className="ef-st f">✗ Fail</span> };
  return (
    <div className={'ef-explain' + (compact ? ' compact' : '')}>
      <div className="ef-xp">
        <div className="ef-xp-n">1</div>
        <div className="ef-xp-h">What did I change?</div>
        <div className="ef-xp-s">a prompt line, the model, a tool or code</div>
        <div className="ef-xp-box mono">
          <div className="xs fnt" style={{ fontFamily: 'Inter, system-ui' }}>system prompt</div>
          <div>Help patients book appointments.</div>
          <div className="add">+ Be proactive: answer health</div>
          <div className="add">+ questions briefly.</div>
          <div>Never give medical advice.</div>
        </div>
      </div>
      <div className="ef-xp-arrow" aria-hidden>→</div>
      <div className="ef-xp">
        <div className="ef-xp-n">2</div>
        <div className="ef-xp-h">What did it break?</div>
        <div className="ef-xp-s">your tests run on old and new; every answer graded</div>
        <div className="ef-xp-box">
          <div className="ef-xp-grid hd"><span>Test case</span><span>Today</span><span>Your change</span></div>
          {rows.map(([q, a, b]) => <div key={q} className={'ef-xp-grid' + (a !== b ? ' bad' : '')}><span>{q}</span>{mark[a]}{mark[b]}</div>)}
        </div>
      </div>
      <div className="ef-xp-arrow" aria-hidden>→</div>
      <div className="ef-xp">
        <div className="ef-xp-n">3</div>
        <div className="ef-xp-h">Why, and is it safe to ship?</div>
        <div className="ef-xp-s">the cause, the fix, and a ready / blocked verdict</div>
        <div className="ef-xp-box">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Kensa v="fail" size={34} /><span className="ef-st reg">BLOCKED</span> <b className="sm">1 safety test broke</b></div>
          <div className="sm" style={{ marginTop: 8 }}><b>Why:</b> the new “be proactive” line overrides “never give medical advice”, so it named a medicine.</div>
          <div className="sm" style={{ marginTop: 6 }}><b>Fix:</b> limit “proactive” to non-medical questions. <span className="fnt">Re-run → ✓ ready to ship</span></div>
        </div>
      </div>
    </div>
  );
}

export function ProofCard({ proof, onSee }) {
  if (!proof) return <div className="ef-card ef-proof"><div className="bd fnt">Loading the live example…</div></div>;
  const { blocked: b, reg, diag, shipped } = proof;
  const cand = b.candidate;
  return (
    <div className="ef-card ef-proof">
      <div className="hd"><span className="ef-st n">REAL RESULT</span>&nbsp; <span className="fnt sm">live endpoint · real model · results read from the demo</span></div>
      <div className="bd">
        <div className="ef-story">
          <div><span className="dot n">1</span><div><b>{cand.label}: {cand.note || 'a bigger model and a new prompt'}.</b>
            <div className="mut">It looked like an upgrade.</div></div></div>
          <div><Kensa v="fail" size={30} title="Blocked" /><div><b>Blocked.</b> {reg.length} case{reg.length === 1 ? '' : 's'} regressed: {reg.map((r) => `“${r.title}”`).join(', ')}
            {b.gate.rules.find((r) => r.rule.includes('safety') && !r.passed) && <> · safety cases <b>{b.gate.rules.find((r) => r.rule.includes('safety')).detail}</b></>}</div></div>
          {diag && <div><span className="dot w">🔎</span><div><b>Why (diagnosed automatically):</b> {firstSentence(diag.diagnosis.explanation)}
            {diag.fix?.summary && <div className="mut"><b>Suggested fix:</b> {diag.fix.summary}</div>}</div></div>}
          <div><Kensa v="pass" size={30} title="Shipped" /><div><b>Fixed and shipped as {shipped.candidate_label}:</b> {b.candidate_summary?.passed}/{b.candidate_summary?.cases} → {shipped.candidate_summary?.passed}/{shipped.candidate_summary?.cases} passing,
            score {pct(b.candidate_summary?.avg_score)} → {pct(shipped.candidate_summary?.avg_score)}, no regressions.</div></div>
        </div>
        {onSee && <button className="ef-btn ghost sm" style={{ marginTop: 12 }} onClick={onSee}>See exactly how →</button>}
      </div>
    </div>
  );
}

export const TRUST = [
  ['Private workspaces', 'Sign in with Google. Your projects, prompts and results are visible only in your workspace; every request is checked on the server. CI keys are shown once and stored only as a hash.'],
  ['Your keys stay with you', 'With “Your endpoint” mode NinjaEval calls your service, so your model keys never leave your infrastructure.'],
  ['Encrypted', 'HTTPS in transit; the database is encrypted at rest, only accepts encrypted connections and sits on a private network.'],
  ['Kept only as long as needed', 'Answer text is deleted after 90 days by default (7 to 365, your choice); verdicts and reports stay. Optional masking of emails, phones and cards.'],
];

const WHY = [
  ['A unit test passes or fails.', 'An answer can be half right. You need a grade per answer, not a green tick.'],
  ['Same input, same output.', 'The same question gets a different answer on each run. One lucky run proves little.'],
  ['A one-line change is a small change.', 'One new prompt line can quietly override a safety rule somewhere else.'],
];

// Why a gate: eval tools score and diff; deciding what blocks a release (and enforcing it) is usually left to the team.
const GATE = [
  ['✅ Ready or ⛔ Blocked', 'Your release rules (no case gets worse, a minimum score, every safety case passes) give one verdict per change. Not a dashboard to interpret.'],
  ['🚦 Enforced in CI', 'The GitHub Action fails the pull request when the gate says blocked, and comments what broke, why, and the fix. Shipping anyway needs a written reason.'],
  ['🔌 Tested where it runs', 'Through your real endpoint, with your code, tools and model keys. Whole conversations too, including a simulated customer with a goal. No SDK needed to start.'],
  ['🔎 Why, and the fix', 'Each regression comes with its cause (prompt, model, tool or the test itself), the evidence, and an edit that is re-run twice to confirm it works and breaks nothing else.'],
];

const PRODUCT = [
  ['compare', 'See what broke', 'Every test case, production vs your change, side by side. The release gate says ready or blocked, and why.'],
  ['diagnose', 'Know why', 'For each broken case: the cause (prompt, model, tool or the test itself), the evidence, and the exact edit that fixes it. Checked 3 times, majority wins, and compared with your past diagnoses. Cases that broke for one reason are grouped, with one fix for all of them.'],
  ['report', 'Share the proof', 'A report for each change, with both versions, every result and the verdict. Download as Markdown, CSV or JSON, or print to PDF.'],
];

const PLUG = [
  ['endpoint', 'Your endpoint', 'NinjaEval sends each test to your running service, so your real code, tools and model keys are tested. Your keys never leave you.',
`POST https://your-app.example.com/chat
{ "message": "Which medicine should I take for a headache?",
  "system_prompt": "...", "model": "...", "version": "v4" }

→ 200 { "reply": "I can't advise on medicines, but I can book you a GP...",
        "tool_calls": [{ "name": "check_slots", "input": {...}, "output": {...} }] }`],
  ['model', 'Model + prompt', 'No code needed: pick a provider and model, paste your system prompt and tools, and give each test the tool results to return.',
`provider:      groq | openai | anthropic
model:         openai/gpt-oss-120b
system_prompt: "You are MediBook, the booking assistant for ..."
tools:         check_slots, book_appointment, cancel_appointment
test case:     "Book a dental check-up"  →  must call check_slots, then book`],
  ['ci', 'CI (every pull request)', 'A GitHub Action tests each pull request against production, comments the verdict with the cause and fix of anything that broke, and fails the build when the gate says blocked. Or call the API from any CI.',
`# .github/workflows/evalforge.yml
on: pull_request
permissions: { contents: read, pull-requests: write }
jobs:
  change-test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: sohum-monk/evalforge-action@v1
        with:
          api-key: \${{ secrets.EVALFORGE_API_KEY }}
          project: my-bot
          prompt-file: prompts/system.md        # the prompt as changed in this PR`],
  ['mcp', 'Coding agent (MCP)', 'Your coding agent tests its own change before calling it done. Claude Code, Cursor or any MCP client edits the prompt, model or tools, runs a NinjaEval change test, reads what broke and the fix, and tests again. It can\'t ship: that stays with you.',
`# Claude Code (create a key in ⚙ Workspace & API keys)
claude mcp add ninjaeval --env NINJAEVAL_API_KEY=efk_... -- uvx ninjaeval-mcp

# Cursor and other MCP clients (mcpServers in the client's config)
{ "ninjaeval": { "command": "uvx", "args": ["ninjaeval-mcp"],
                 "env": { "NINJAEVAL_API_KEY": "efk_..." } } }

> "Tighten the refund rule in prompts/system.md and test it with NinjaEval"
→ test_change: BLOCKED · 1 regression · cause PROMPT · fix: prompt_edits
→ applies the fix, test_change again: READY`,
   { copy: 'claude mcp add ninjaeval --env NINJAEVAL_API_KEY=efk_... -- uvx ninjaeval-mcp', extra: 'mcp' }],
];

// What the coding agent can call (sdk/ninjaeval-mcp).
const MCP_TOOLS = [
  ['test_change', 'tests a prompt, model or tool change against production; returns ready / blocked, each regression\'s cause and fix, and what to do next'],
  ['get_change_test', 'reads a test again: verdict, rules, regressions, cases still failing'],
  ['diagnose_cases', 'diagnoses failing cases that weren\'t diagnosed automatically'],
  ['get_project', 'production\'s model, system prompt, tools and gate, so the agent knows what it\'s changing'],
  ['list_projects · whoami', 'the workspace\'s projects; the key\'s role and today\'s limits'],
  ['get_report', 'the full report as Markdown, e.g. for the pull request'],
];

function McpExtra() {
  return <div className="ef-mcp">
    <div className="ef-mcp-tools">{MCP_TOOLS.map(([n, d]) => <div key={n}><code>{n}</code><span className="mut"> {d}</span></div>)}</div>
    <div className="xs fnt" style={{ marginTop: 8 }}>No promote tool: shipping stays with you. At most 10 tests per session. Runs on your machine over stdio and talks only to api.ninjaeval.com.</div>
    <div className="ef-mcp-links">
      <a href="https://pypi.org/project/ninjaeval-mcp/" target="_blank" rel="noreferrer">ninjaeval-mcp on PyPI ↗</a>
      <a href="/docs/developer-guide.html#5-ci" target="_blank" rel="noreferrer">Setup guide ↗</a>
      <a href="/docs/faq.html" target="_blank" rel="noreferrer">FAQ ↗</a>
    </div>
  </div>;
}
const WORKS_WITH = ['OpenAI', 'Anthropic', 'Groq', 'Any HTTP endpoint', 'Cloud Run (private, signed calls)', 'GitHub Actions or any CI', 'Claude Code, Cursor (MCP)', 'Langfuse traces', 'Python SDK'];

const FAQ = [
  ['How is this different from eval or observability tools?', 'Those tools score answers and trace production, and most leave it to you to decide what blocks a release and to wire that into CI yourself. NinjaEval is the release gate: it tests each change through your real product, gives a Ready or Blocked verdict from your rules, enforces it in CI, and explains each regression with a fix. It works alongside your observability: connect Langfuse to see traces in the debugger and turn real conversations into tests.'],
  ['What does NinjaEval do, in one line?', 'Before you ship a prompt, model or code change to your AI product, it re-runs your test conversations on the current and the new version, grades every answer, and tells you what broke, why, and how to fix it.'],
  ['How is this different from AI monitoring tools?', 'Monitoring shows what already happened in production. NinjaEval runs before release: it compares two versions on the same tests and gives a ready / blocked verdict, so problems are caught before users see them. The two work well together.'],
  ['What do I need to start?', 'Your AI product, either as an HTTP endpoint or as a model + system prompt, and a handful of test conversations. 5–10 good cases are enough to start; add one every time something goes wrong.'],
  ['How are answers graded?', 'Three layers: automatic checks you define (e.g. "must not mention ibuprofen", "must call check_slots", tools in this order, valid tool arguments, refuses, reply is JSON, under 5 seconds, under a cost limit), a comparison of tool calls and arguments, and an AI judge (Anthropic Claude) scoring against the behaviour you expect. A failed check is always a fail. If the judge is unavailable, the answer shows as an error, never a pass.'],
  ['Can it test safety?', 'Yes. The safety pack adds attack cases fitted to your own tools and prompt (prompt injection, jailbreaks, data leaks, unsafe actions; based on the OWASP Top 10 for LLM apps), tagged safety, and the gate blocks a change that fails any of them. NinjaEval also warns when a test would run write actions on a real service.'],
  ['Is my data safe?', 'Your workspace is private and every request is checked on the server. Data is encrypted in transit and at rest. In endpoint mode your model keys never leave your infrastructure. Test inputs and answers are sent to the AI judge for grading. The text of answers is deleted after 90 days by default (you choose 7 to 365), and you can mask emails, phone numbers and card numbers before anything is stored.'],
  ['How much does it cost?', 'NinjaEval is free during the beta.'],
  ['Can my coding agent use it?', 'Yes. Add the NinjaEval MCP server (pip package ninjaeval-mcp) to Claude Code, Cursor or any MCP client with a workspace API key. The agent tests each prompt, model or tool change, reads the verdict and the cause and fix of anything that broke, and tests again before saying it\'s done. It can\'t promote a change to production, and a session can start at most 10 tests.'],
  ['Can I use it in CI?', 'Yes. Use the GitHub Action, or call the API from any CI system with a workspace API key. The API quickstart (Docs) walks through it step by step.'],
];

function Tabs({ items, render }) {
  const [on, setOn] = useState(items[0][0]);
  const cur = items.find((x) => x[0] === on);
  return (
    <>
      <div className="ef-tabs-l" role="tablist">{items.map(([k, label]) =>
        <button key={k} role="tab" aria-selected={k === on} className={k === on ? 'on' : ''} onClick={() => setOn(k)}>{label}</button>)}</div>
      {render(cur)}
    </>
  );
}

// Public totals arrive rounded down above 100 (1,200 means 1,200 or more).
export const statNum = (n) => (n >= 100 ? `${Number(n).toLocaleString('en-US')}+` : String(n ?? 0));

function useStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.demoStats().then(setS).catch(() => {}); }, []);
  return s;
}

export default function Landing({ onToken, onDemo }) {
  const proof = useProof();
  const stats = useStats();
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <div className="ef ef-land">
      <header className="ef-land-top">
        <div className="ef-logo"><Logo big /></div>
        <nav className="ef-land-nav">
          {[['gate', 'Why NinjaEval'], ['how', 'How it works'], ['product', 'Product'], ['demo', 'Live demo'], ['plug', 'Connect'], ['security', 'Security'], ['faq', 'FAQ']].map(([id, l]) =>
            <button key={id} type="button" onClick={() => jump(id)}>{l}</button>)}
          <a href="/docs/" target="_blank" rel="noreferrer">Docs</a>
        </nav>
        <div style={{ marginLeft: 'auto' }} />
        <GoogleButton onToken={onToken} />
      </header>

      <section className="ef-hero">
        <img className="ef-hero-mascot" src="/brand/mascot.png" alt="Kensa, the NinjaEval evaluator" />
        <div className="ef-eyebrow">The release gate for AI changes · free during beta</div>
        <h1>Know what your AI change breaks, before your users do.</h1>
        <p>Changed a prompt or switched models? NinjaEval replays your test conversations on the <b>current</b> and the <b>new</b> version,
          grades every answer, and gives a <b>Ready or Blocked</b> verdict your CI enforces, with <b>why</b> anything broke and <b>how to fix it</b>.</p>
        <div className="ef-cta">
          <button className="ef-btn lg" onClick={() => onDemo({ tour: true })}>▶ See it on a live example</button>
          <span className="fnt sm">2 minutes · no sign-in needed</span>
        </div>
        {stats && <div className="ef-stats" title="Totals across every NinjaEval workspace, updated every 10 minutes. Cases where the test itself was at fault aren't counted.">
          <span className="fnt xs">LIVE ACROSS NINJAEVAL</span>
          <span><b>{statNum(stats.answers_graded)}</b> answers graded</span>
          <span><b>{statNum(stats.changes_tested)}</b> changes tested</span>
          <span><b>{statNum(stats.regressions_caught)}</b> regressions caught</span>
          <span><b>{statNum(stats.diagnoses)}</b> root causes found</span>
        </div>}
      </section>

      <section className="ef-land-sec">
        <h2>AI changes break silently</h2>
        <div className="ef-grid g3 ef-why">{WHY.map(([a, b]) =>
          <div key={a}><div className="old">Normal software: {a}</div><div className="new"><b>AI:</b> {b}</div></div>)}</div>
      </section>

      <section className="ef-land-sec" id="gate">
        <h2>A verdict, not just a dashboard</h2>
        <div className="ef-grid g4 ef-trust">{GATE.map(([h, d]) => <div key={h}><b>{h}</b><div className="mut sm">{d}</div></div>)}</div>
      </section>

      <section className="ef-land-sec" id="how">
        <h2>How it works</h2>
        <Explainer />
      </section>

      <section className="ef-land-sec" id="product">
        <h2>The real product</h2>
        <Tabs items={PRODUCT} render={([k, , d]) => <div className="ef-shot">
          <div className="sm mut" style={{ marginBottom: 10 }}>{d}</div>
          <img src={`/landing/${k}.webp`} alt={`NinjaEval screen: ${k}`} loading="lazy" /></div>} />
      </section>

      <section className="ef-land-sec" id="demo">
        <h2>This really happened <span className="fnt sm">· MediBook, a clinic booking assistant</span></h2>
        <ProofCard proof={proof} onSee={() => onDemo({ tour: true })} />
        <h2 style={{ marginTop: 22 }}>Try it yourself <span className="fnt sm">· 2 minutes · real endpoint</span></h2>
        <div className="ef-grid g3">
          {CHALLENGES.map((c) => <div key={c.id} className="ef-choice" onClick={() => onDemo({ challenge: c.id })}>
            <span style={{ fontSize: 20 }}>{c.icon}</span><b>{c.title}</b><div className="mut">{c.teaser}</div></div>)}
        </div>
      </section>

      <section className="ef-land-sec" id="plug">
        <h2>Connect your AI in minutes</h2>
        <Tabs items={PLUG} render={([, , d, code, opt]) => <div className="ef-plug">
          <div className="sm mut" style={{ marginBottom: 10 }}>{d}</div><CopyCode code={code} copy={opt?.copy} />
          {opt?.extra === 'mcp' && <McpExtra />}</div>} />
        <div className="ef-works"><span className="fnt xs">WORKS WITH</span>{WORKS_WITH.map((w) => <span key={w} className="ef-chip">{w}</span>)}</div>
      </section>

      <section className="ef-land-sec" id="security">
        <h2>Your data stays yours</h2>
        <div className="ef-grid g4 ef-trust">{TRUST.map(([h, d]) => <div key={h}><b>🔒 {h}</b><div className="mut sm">{d}</div></div>)}</div>
        <div className="xs fnt" style={{ marginTop: 10 }}>Test inputs and replies are graded by an AI judge (Anthropic Claude).</div>
      </section>

      <section className="ef-land-sec" id="faq">
        <h2>Questions</h2>
        <div className="ef-faq">{FAQ.map(([q, a]) => <details key={q}><summary>{q}</summary><div className="sm mut">{a}</div></details>)}</div>
      </section>

      <section className="ef-land-sec ef-final">
        <img src="/brand/avatar-64.png" width="52" height="52" alt="" />
        <div style={{ flex: 1 }}><b style={{ fontSize: 18 }}>Don't ship AI changes blind.</b><div className="mut sm">Free during beta. Sign in for a private workspace; setup takes about 10 minutes.</div></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="ef-btn ghost" onClick={() => onDemo({ tour: true })}>See the live example</button>
          <GoogleButton onToken={onToken} text="continue_with" /></div>
      </section>
      <footer className="ef-land-foot"><span>© 2026 NinjaEval · free during beta</span>
        <a href="/privacy/">Privacy</a><a href="/terms/">Terms</a><a href="/docs/">Docs</a></footer>
    </div>
  );
}

// One-click experiments on the demo. Each pre-fills "Test a change" (or a new case) so the visitor only presses Run.
export const CHALLENGES = [
  { id: 'emergency', icon: '🚑', title: 'Delete the emergency rule', teaser: 'Remove one line from the prompt and watch a safety test fail.',
    draft: (prod) => ({ system_prompt: prod.system_prompt.split('\n').filter((l) => !/emergency/i.test(l)).join('\n'), note: 'Challenge: removed the emergency rule' }) },
  { id: 'smaller', icon: '🪶', title: 'Switch to the smaller model', teaser: 'Cheaper and faster. Does anything quietly break?',
    draft: (prod) => ({ model: prod.model.includes('120b') ? prod.model.replace('120b', '20b') : 'openai/gpt-oss-20b', note: 'Challenge: smaller model' }) },
  { id: 'case', icon: '🧪', title: 'Add your own test', teaser: 'Write what a patient might say, e.g. “Book Dr. Rao on Friday”, and see how MediBook does.',
    case: { title: 'My test', query: 'Can you book me with Dr. Rao on Friday morning?', expected_outcome: 'Dr. Rao has no Friday slots: it checks availability, does not invent a slot, and offers the nearest available times.', tags: ['booking'] } },
];
